import React from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Close from "@mui/icons-material/Close";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const getListStyle = isDraggingOver => ({
  background: isDraggingOver ? "#f5f5f5" : "white",
  borderRadius: "4px",
  padding: "8px",
});

const getItemStyle = (isDragging, isExcluded, draggableStyle) => ({
  userSelect: "none",
  background: isExcluded ? "#fff3e0" : isDragging ? "#e3f2fd" : "white",
  border: isExcluded
    ? "1px dashed #fb8c00"
    : isDragging
    ? "1px solid #1976d2"
    : "1px solid #e0e0e0",
  opacity: isExcluded ? 0.6 : 1,
  borderRadius: "4px",
  ...draggableStyle,
});

const styles = {
  listItem: {
    marginBottom: 0.5,
  },
  listItemButton: {
    py: 0.5,
    cursor: "grab",
  },
  listItemText: {
    flex: 1,
  },
  iconButton: {
    ml: 1,
  },
};

const VerticalDraggableList = ({ tags, onRemove, onChange, maxFields = Infinity }) => {
  const onDragEnd = result => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    const draggedTag = tags[sourceIndex];

    // Prevent mandatory fields from being dragged to yellow area (beyond maxFields)
    if (draggedTag.isMandatory && destinationIndex >= maxFields) {
      return; // Reject the drag - field will snap back
    }

    const reorderedTags = reorder(tags, sourceIndex, destinationIndex);
    onChange(reorderedTags);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="vertical-draggable-list">
        {(provided, snapshot) => (
          <List
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}
            dense
          >
            {tags.map((tag, index) => (
              <Draggable key={tag.id} draggableId={tag.id.toString()} index={index}>
                {(provided, snapshot) => (
                  <ListItem
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    disablePadding
                    style={getItemStyle(snapshot.isDragging, tag.excluded, provided.draggableProps.style)}
                    sx={styles.listItem}
                  >
                    <ListItemButton sx={styles.listItemButton} {...provided.dragHandleProps}>
                      <ListItemText
                        primary={tag.content}
                        sx={styles.listItemText}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                      {!tag.isMandatory && (
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            onRemove(tag.id);
                          }}
                          sx={styles.iconButton}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      )}
                    </ListItemButton>
                  </ListItem>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default VerticalDraggableList;
